import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { observer } from 'mobx-react';
import { useBlocker, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Breadcrumb,
  CRUD_COMPONENT_TYPE,
  CRUDComponent,
  FormSearchResultAdvanced,
  createSearchState,
  formListValueFormatter,
  useDataProvider,
  useLocalization,
  useStore,
  useUserAccess,
  UnSavedChangesDialog,
} from '@ui-framework/index';
import {
  FormFields,
  IconLibrary,
  Layout,
  PageSection,
  StackablePageButtons,
  getDefaultValueByControl,
} from '@product-framework/index';
import { listSchema, searchSchema } from '../../schemas';
import {
  ISSUE_API_ROOT,
  INITIAL_LOOKUPS,
  ISSUE_LOOKUP_CATEGORIES,
  ISSUES_LIST_STORE_NAME,
  SXCL_SERVICES_API_ROOT,
} from '../../components/constants';
import IssueCreateDialog from '../../components/IssueCreateDialog';
import useIssuePermissions from '../../components/useIssuePermissions';
import {
  IssueAssigneeSelector,
  IssuePrioritySelect,
  IssueReporterCompanySelector,
  IssueReporterCompanyTypeSelect,
  IssueReporterSelector,
  IssueServiceAdministratorSelector,
  IssueStatusSelect,
  IssueTypeSelect,
} from '../../components/IssueLookupSelect';
import IssueStatusSummary from '../../components/IssueStatusSummary';
import {
  filtersToApiParams,
  getCompanyNameLabel,
  getCurrentUserId,
  getIssueData,
  getIssueUserOptionLabel,
  getLocalizedLookupLabel,
  getProviderName,
  getReporterDisplayLabel,
  humanizeCompanyType,
} from '../../components/utils';
import {
  assignedTo,
  company,
  createdDateTime,
  issueNumber,
  lastModifiedDateTime,
  priorityCode,
  reporter,
  reporterCompanyType,
  statusCode,
  title,
  typeCode,
} from './accessorFunctions';
import layoutIssueSearchFields from './utils';
import validationSchema from './validations';

function getStatusFilter(filters = []) {
  return filters.find(filter => filter.field === 'statusCode')?.value || '';
}

const hasOwn = (object, key) =>
  Object.prototype.hasOwnProperty.call(object, key);

const getFilterValue = value =>
  value && typeof value === 'object' && hasOwn(value, 'value')
    ? value.value
    : value;

const getFilterOperator = filter =>
  filter?.value &&
  typeof filter.value === 'object' &&
  hasOwn(filter.value, 'operator')
    ? filter.value.operator
    : filter?.operator;

function setStatusFilterInState(currentState, nextStatusCode) {
  const nextFilters = (currentState.filters || []).filter(
    filter => filter.field !== 'statusCode',
  );

  if (nextStatusCode) {
    nextFilters.push({
      field: 'statusCode',
      operator: 'eq',
      value: nextStatusCode,
    });
  }

  return {
    ...currentState,
    paginationState: {
      ...currentState.paginationState,
      pageIndex: 0,
    },
    filters: nextFilters,
  };
}

function buildSortParam({ sortingState = [] }) {
  if (Array.isArray(sortingState) && sortingState.length > 0) {
    return sortingState
      .map(
        ({ id, desc }) => `${id.replace('[.]', '.')}:${desc ? 'DESC' : 'ASC'}`,
      )
      .join(',');
  }

  return '';
}

function addListFilterParams(apiUrl, filters = []) {
  filters.forEach(filter => {
    const operator = getFilterOperator(filter);
    const filterValue = getFilterValue(filter?.value);

    apiUrl.searchParams.set(
      `${filter?.searchAs || filter?.field}`,
      `$${operator === 'eqNull' ? 'eq' : operator}:${
        operator === 'eqNull' ? null : filterValue
      }`,
    );
  });
}

function buildIssueListCacheKey({
  listApiUrl,
  filters,
  paginationState,
  sortingState,
  statusCode: nextStatusCode,
}) {
  let apiUrl;
  try {
    apiUrl = new URL(listApiUrl);
  } catch {
    return listApiUrl;
  }
  const nextFilters = (filters || []).filter(
    filter => filter.field !== 'statusCode',
  );

  if (nextStatusCode) {
    nextFilters.push({
      field: 'statusCode',
      operator: 'eq',
      value: nextStatusCode,
    });
  }

  apiUrl.searchParams.set('page', `${paginationState.pageIndex + 1 || 1}`);
  apiUrl.searchParams.set('limit', `${paginationState?.pageSize || 10}`);
  addListFilterParams(apiUrl, nextFilters);

  const sort = buildSortParam({ sortingState });
  if (sort) {
    apiUrl.searchParams.set('sortBy', sort);
  }

  return apiUrl.href;
}

function buildEmptyIssueListResponse(page, limit) {
  return {
    ok: true,
    data: {
      data: [],
      page,
      limit,
      total: 0,
      totalCount: 0,
      totalPages: 0,
    },
  };
}

function IssuesList() {
  const { formatMessage } = useLocalization();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showSearch, setShowSearch] = useState(false);
  const [resultsFound, setResultsFound] = useState(null);
  const { actions: issueActions, fields: issueFieldPermissions } =
    useIssuePermissions();
  const canCreateIssue = issueActions.includes('CREATE');
  const [createOpen, setCreateOpen] = useState(false);
  const [createDialogDirty, setCreateDialogDirty] = useState(false);
  const [lookups, setLookups] = useState(INITIAL_LOOKUPS);
  const [issueConfig, setIssueConfig] = useState(null);
  const [statusCounts, setStatusCounts] = useState({});
  const [listRefreshKey, setListRefreshKey] = useState(0);
  const issueListResponseCache = useRef(new Map());
  const statusCountsRequestId = useRef(0);
  const latestListViewStateRef = useRef({
    paginationState: { pageIndex: 0, pageSize: 10 },
    sortingState: [],
  });
  const store = useStore();
  const userAccess = useUserAccess();
  const dataProvider = useDataProvider(getProviderName());
  const sxclServicesDataProvider = useDataProvider('sxclServices');
  const createDialogBlocker = useBlocker(createOpen && createDialogDirty);

  const baseListApiUrl = `${dataProvider.getApiUrl()}${ISSUE_API_ROOT}`;
  const listState = store.domain.list.get(ISSUES_LIST_STORE_NAME);
  latestListViewStateRef.current = {
    paginationState: listState.paginationState,
    sortingState: listState.sortingState,
  };
  const showServiceAdministratorColumn =
    (userAccess.isAdmin?.() ?? false) || (userAccess.isSA?.() ?? false);
  const currentUserId = getCurrentUserId(userAccess);
  const listApiUrl = baseListApiUrl;
  const listFiltersWithoutStatus = (listState.filters || []).filter(
    filter => filter.field !== 'statusCode',
  );
  const listFiltersWithoutStatusKey = JSON.stringify(listFiltersWithoutStatus);
  const statusLookupsKey = JSON.stringify(lookups.STATUS || []);
  const selectedStatusCode = getStatusFilter(listState.filters);
  const issueListSchema = useMemo(() => {
    // Assignment is SA/Admin-internal triage detail; Service Providers never
    // see who a request is assigned to (SXCL-2475).
    const hiddenColumns = new Set(
      showServiceAdministratorColumn ? [] : ['reporterCompanyType'],
    );
    if (userAccess.isSP?.()) {
      hiddenColumns.add('assignedTo');
    }
    return listSchema.filter(({ name }) => !hiddenColumns.has(name));
  }, [showServiceAdministratorColumn, userAccess]);
  const issueSearchSchema = useMemo(() => {
    const hiddenFields = new Set();
    if (userAccess.isSP?.()) {
      hiddenFields.add('assigneeUserId');
      hiddenFields.add('companyId');
      hiddenFields.add('reporterCompanyType');
      hiddenFields.add('serviceAdministratorId');
    } else if (userAccess.isSA?.()) {
      hiddenFields.add('serviceAdministratorId');
    }
    return layoutIssueSearchFields(searchSchema, hiddenFields);
  }, [userAccess]);

  const loadIssuePageData = useCallback(async () => {
    const [configResult, ...lookupResults] = await Promise.all([
      dataProvider.get(`${ISSUE_API_ROOT}/config`),
      ...ISSUE_LOOKUP_CATEGORIES.map(category =>
        dataProvider.get(`${ISSUE_API_ROOT}/lookups`, { category }),
      ),
    ]);

    setIssueConfig(
      configResult.ok ? (configResult.data?.data ?? configResult.data) : null,
    );
    setLookups(
      ISSUE_LOOKUP_CATEGORIES.reduce((nextLookups, category, index) => {
        const result = lookupResults[index];
        return {
          ...nextLookups,
          [category]: result.ok ? result.data.data || [] : [],
        };
      }, {}),
    );
  }, [dataProvider]);

  const loadStatusCounts = useCallback(async () => {
    const requestId = statusCountsRequestId.current + 1;
    statusCountsRequestId.current = requestId;
    const statuses = lookups.STATUS;
    if (statuses.length === 0) {
      setStatusCounts({});
      return;
    }

    const baseFilters = filtersToApiParams(listFiltersWithoutStatus);
    const result = await dataProvider.get(`${ISSUE_API_ROOT}/stats`, {
      ...baseFilters,
    });

    if (requestId !== statusCountsRequestId.current) {
      return;
    }

    if (!result.ok) {
      setStatusCounts({});
      return;
    }

    const nextStatusCounts = {
      ALL: result.data?.total || 0,
      ...(result.data?.byStatus || {}),
    };

    setStatusCounts(nextStatusCounts);

    const { paginationState, sortingState } = latestListViewStateRef.current;
    const emptyResponse = buildEmptyIssueListResponse(
      1,
      paginationState?.pageSize || 10,
    );
    const seedEmptyCache = statusCodeToCache => {
      issueListResponseCache.current.set(
        buildIssueListCacheKey({
          listApiUrl,
          filters: listFiltersWithoutStatus,
          paginationState: {
            ...paginationState,
            pageIndex: 0,
          },
          sortingState,
          statusCode: statusCodeToCache,
        }),
        emptyResponse,
      );
    };

    if (!nextStatusCounts.ALL) {
      seedEmptyCache('');
    }

    statuses.forEach(status => {
      if (!nextStatusCounts[status.code]) {
        seedEmptyCache(status.code);
      }
    });
  }, [dataProvider, listApiUrl, listFiltersWithoutStatusKey, statusLookupsKey]);

  useEffect(() => {
    loadIssuePageData();
  }, [loadIssuePageData]);

  useEffect(() => {
    loadStatusCounts();

    return () => {
      statusCountsRequestId.current += 1;
    };
  }, [loadStatusCounts]);

  useEffect(() => {
    setCreateOpen(
      canCreateIssue &&
        searchParams.get('create') === 'true' &&
        !searchParams.get('issueId'),
    );
  }, [canCreateIssue, searchParams]);

  useEffect(() => {
    const issueId = searchParams.get('issueId');
    if (issueId) {
      navigate(`/issue-tracking/edit/${issueId}`, { replace: true });
    }
  }, [navigate, searchParams]);

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => {
        navigate('/dashboard');
      },
    },
    {
      name: formatMessage({
        id: 'LIST_REQUESTS',
        defaultMessage: 'List Requests',
      }),
      icon: IconLibrary.Issues,
      onClick: undefined,
    },
  ];

  const stackablePageButtons = [
    ...(canCreateIssue
      ? [
          {
            label: formatMessage({
              id: 'SUBMIT_REQUEST',
              defaultMessage: 'Submit Request',
            }),
            Icon: IconLibrary.IssueCreate,
            variant: 'outlined',
            onClick: () => {
              setSearchParams({ create: 'true' });
            },
          },
        ]
      : []),
    {
      label: !showSearch
        ? formatMessage({
            id: 'SHOW_SEARCH',
            defaultMessage: 'Show Search',
          })
        : formatMessage({
            id: 'HIDE_SEARCH',
            defaultMessage: 'Hide Search',
          }),
      Icon: !showSearch ? IconLibrary.Search : IconLibrary.HideSearch,
      variant: 'contained',
      onClick: () => {
        setShowSearch(!showSearch);
      },
    },
  ];

  const preProcessSearchRequestData = data =>
    Object.fromEntries(
      Object.entries(data).filter(
        ([, value]) => value !== undefined && value !== null && value !== '',
      ),
    );

  const submitSearchForm = async data => {
    const requestData = preProcessSearchRequestData(data);
    const searchState = createSearchState(requestData, issueSearchSchema);
    issueListResponseCache.current.clear();
    store.domain.list.set(ISSUES_LIST_STORE_NAME, searchState);
  };

  const openIssuePage = issue => {
    if (issue?.id) {
      navigate(`/issue-tracking/edit/${issue.id}`, { state: { issueConfig } });
    }
  };

  const customComponents = {
    ...FormFields,
    IssueAssigneeSelector,
    IssuePrioritySelect,
    IssueReporterCompanySelector,
    IssueReporterCompanyTypeSelect,
    IssueReporterSelector,
    IssueServiceAdministratorSelector,
    IssueStatusSelect,
    IssueTypeSelect,
  };

  const listProps = {
    columnPrimaryId: 'id',
    storeName: ISSUES_LIST_STORE_NAME,
    dataProvider,
    schema: issueListSchema,
    initialState: {
      showColumnFilters: false,
    },
    downloadApiUrl: null,
    listApiUrl,
    listResponseCache: issueListResponseCache.current,
    refreshKey: `${listRefreshKey}:${currentUserId || ''}`,
    accessorFunctions: {
      assignedTo,
      company,
      createdDateTime,
      issueNumber,
      lastModifiedDateTime,
      priorityCode: original => priorityCode(original, formatMessage),
      reporter,
      reporterCompanyType: original =>
        reporterCompanyType(original, formatMessage),
      statusCode: original => statusCode(original, formatMessage),
      title,
      typeCode: original => typeCode(original, formatMessage),
    },
    accessorFunctionsAdditional: {
      openIssueDetail: openIssuePage,
    },
    onRowCountChange: rowCount => {
      setResultsFound(rowCount);
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => openIssuePage(row.original),
      sx: {
        cursor: 'pointer',
      },
    }),
  };

  const searchProps = {
    customComponents,
    storeName: ISSUES_LIST_STORE_NAME,
    IconLibrary,
    schema: issueSearchSchema,
    onSubmit: submitSearchForm,
    onClear: () => {
      issueListResponseCache.current.clear();
      store.domain.list.reset(ISSUES_LIST_STORE_NAME);
    },
    validationSchema: validationSchema(formatMessage),
    hideSearchForm: () => {
      setShowSearch(false);
    },
    getValueByControl: getDefaultValueByControl,
  };

  const preProcessSearchValues = searchedParams => {
    const lookupLabel = category => value => {
      const filterValue = getFilterValue(value);
      const lookup = (lookups[category] || []).find(
        entry => entry.code === filterValue,
      );
      return (
        getLocalizedLookupLabel(
          lookup || filterValue,
          category,
          formatMessage,
        ) || filterValue
      );
    };

    const formatterMap = {
      assigneeUserId: async value => {
        const filterValue = getFilterValue(value);
        const result = await dataProvider.get(`${ISSUE_API_ROOT}/users`, {
          userId: filterValue,
          assignableOnly: true,
        });
        const issueUser = getIssueData(result)?.[0];
        const displayName = getIssueUserOptionLabel(issueUser);
        return result.ok && displayName ? displayName : filterValue;
      },
      priorityCode: lookupLabel('PRIORITY'),
      companyId: async value => {
        const filterValue = getFilterValue(value);
        const result = await dataProvider.get(`${ISSUE_API_ROOT}/companies`, {
          companyId: filterValue,
          page: 1,
          limit: 1,
        });
        const issueCompany = getIssueData(result)?.[0];
        return result.ok
          ? getCompanyNameLabel(issueCompany) || filterValue
          : filterValue;
      },
      reporterCompanyType: value => {
        const filterValue = getFilterValue(value);
        return humanizeCompanyType(filterValue, formatMessage) || filterValue;
      },
      reporterUserId: async value => {
        const filterValue = getFilterValue(value);
        const result = await dataProvider.get(`${ISSUE_API_ROOT}/users`, {
          userId: filterValue,
        });
        const issueUser = getIssueData(result)?.[0];
        return result.ok
          ? getReporterDisplayLabel(issueUser) || filterValue
          : filterValue;
      },
      serviceAdministratorId: async value => {
        const filterValue = getFilterValue(value);
        const result = await sxclServicesDataProvider.get(
          `${SXCL_SERVICES_API_ROOT}/service-administrators/${filterValue}`,
        );
        const serviceAdministrator = result.data?.data || result.data;
        return result.ok
          ? serviceAdministrator?.serviceAdministratorName ||
              serviceAdministrator?.serviceAdministratorAbbreviation ||
              filterValue
          : filterValue;
      },
      statusCode: lookupLabel('STATUS'),
      typeCode: lookupLabel('TYPE'),
    };

    return formListValueFormatter({
      data: searchedParams || [],
      formatterMap,
    });
  };

  const searchParamsProps = {
    preProcessSearchValues,
    storeName: ISSUES_LIST_STORE_NAME,
    schema: issueSearchSchema,
    resultsFound,
    valueFormatter: ({ value }) => value,
  };

  const closeCreate = () => {
    setCreateOpen(false);
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete('create');
    setSearchParams(nextSearchParams);
  };

  const stayOnCreateDialog = () => {
    createDialogBlocker.reset?.();
  };

  const leaveCreateDialog = () => {
    setCreateDialogDirty(false);
    createDialogBlocker.proceed?.();
  };

  const refreshIssues = () => {
    issueListResponseCache.current.clear();
    loadStatusCounts();
    setListRefreshKey(value => value + 1);
  };

  const onStatusSummaryChange = nextStatusCode => {
    store.domain.list.set(
      ISSUES_LIST_STORE_NAME,
      setStatusFilterInState(listState, nextStatusCode),
    );
  };

  return (
    <Layout>
      <Grid2 xs={12} container>
        <Grid2 xs={12} md={6}>
          <Breadcrumb items={breadCrumb} />
        </Grid2>
        <Grid2 xs={12} md={6}>
          <StackablePageButtons buttons={stackablePageButtons} />
        </Grid2>
      </Grid2>
      <Grid2 xs={12}>
        <FormSearchResultAdvanced {...searchParamsProps} />
      </Grid2>

      {showSearch && (
        <PageSection
          containerProps={{ sx: { mb: 3 } }}
          label={formatMessage({
            id: 'SEARCH_REQUESTS',
            defaultMessage: 'Search Requests',
          })}>
          <Box>
            <CRUDComponent
              type={CRUD_COMPONENT_TYPE.SEARCH_ADVANCED}
              searchProps={searchProps}
            />
          </Box>
        </PageSection>
      )}

      <PageSection
        label={formatMessage({
          id: 'REQUESTS',
          defaultMessage: 'Requests',
        })}>
        <IssueStatusSummary
          statuses={lookups.STATUS.map(status => ({
            ...status,
            label: getLocalizedLookupLabel(status, 'STATUS', formatMessage),
          }))}
          statusCounts={statusCounts}
          selectedStatusCode={selectedStatusCode}
          onStatusChange={onStatusSummaryChange}
          excludedStatusCodes={issueConfig?.terminalStatusCodes}
        />
        <Box>
          <CRUDComponent
            key={`issues-list:${currentUserId || ''}`}
            type={CRUD_COMPONENT_TYPE.ADVANCED_LIST}
            listProps={listProps}
          />
        </Box>
      </PageSection>

      <IssueCreateDialog
        open={createOpen}
        onClose={closeCreate}
        onCreated={refreshIssues}
        dataProvider={dataProvider}
        issueConfig={issueConfig}
        fieldPermissions={issueFieldPermissions}
        onDirtyChange={setCreateDialogDirty}
      />
      <UnSavedChangesDialog
        open={createDialogBlocker.state === 'blocked'}
        onStay={stayOnCreateDialog}
        onLeave={leaveCreateDialog}
      />
    </Layout>
  );
}

export default observer(IssuesList);
